import { ChangeEvent } from "react";
import { ItemDto } from "src/dtos/item/itemDto";

import { DropDownForm } from "src/components/common/form/DropdownForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { useMST1010_R01Store } from "./Store_MST1010_R01";
import { ImageForm } from "src/components/common/form/ImageForm";
import { defaultMST1010_R01Dto } from "./Dto_MST1010_R01";
import { searchCustomer } from "src/apis/customerApi";
import { searchLocation } from "src/apis/locationApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import useUiuxStore from "src/store/useUiuxStore";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { CodeForm } from "src/components/common/form/CodeForm";
import { FlexFormBreak } from "src/components/common/common";

interface Props {
		data?: ItemDto;
}

export const Info_MST1010_R01 = ({ data }: Props) => {

	const { setCodeSearchModalProps } = useUiuxStore();
	const { selectedMST1010_R01, editMST1010_R01, setEditMST1010_R01 } = useMST1010_R01Store();
	const { brandCdList, itemAccountList, itemGroupList, prodStockTypeList, unitList, yesNoList } = useCommonCodeStore();

	const custInfoHandleFoucsId = 'infoCustInput';
	const locationInfoHandleFoucsId = 'infoLocationInput';

	const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		if (!editMST1010_R01) return;

		if(name === 'locationCd') {
			if(value === '') {
				setEditMST1010_R01({ ...editMST1010_R01, locationCd: '', locationNm: '' });
				return;
			}
			// 값이 변경되었고, 기존 값과 다르면 nm 초기화
			if(value !== editMST1010_R01?.locationCd) {
				setEditMST1010_R01({ ...editMST1010_R01, locationCd: value, locationNm: '' });
				return;
			}
		}

		if (name === 'itemCost') {
			const n = Number(value);
			if (!Number.isFinite(n)) return;
			setEditMST1010_R01({
				...editMST1010_R01,
				itemCost: n,
				stockAmount: n * (editMST1010_R01.stockQty ?? 0),
			});
			return;
		}

		setEditMST1010_R01({ ...editMST1010_R01, [name]: value, stockAmount: (editMST1010_R01.itemCost ?? 0) * (editMST1010_R01.stockQty ?? 0) });
	};

	
	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				post: 'post',
				address: 'address',
				detailAddress: 'detailAddress',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd',
				taxEmail: 'email',
			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editMST1010_R01?.customCd || '',
				customNm: editMST1010_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'locationCd',
			category: '보관위치',
			focusId: locationInfoHandleFoucsId,
			responseMapping: {
				locationCd: 'locationCd',
				locationNm: 'locationNm'
			},
			searchFunction: async (searchText: string) => {
				const locationResponse = await searchLocation(searchText);
				return locationResponse.data.data.find(value => value.locationCd === searchText);
			},
			extraFilter: () => ({
				locationCd: editMST1010_R01?.locationCd || '',
				locationNm: editMST1010_R01?.locationNm || '',
			}),
		}
	];

	
	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editMST1010_R01,
		setEditData: setEditMST1010_R01,
		defaultData: defaultMST1010_R01Dto,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'MST1010_R01'
	});

	// InputForm 바깥 flex에 제목+입력이 가로로 붙으므로, wrapper를 100px로 고정하면 안쪽 min-w와 충돌해 겹침 발생
	const infoInputStyle = {
		wrapperStyle: "w-fit min-w-[300px] mr-[10px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px] shrink-0 text-left",
		inputStyle: "w-[100px] min-w-[100px] shrink-0 text-right",
	}

	const itemImageWrapperStyle = {
		wrapperStyle: "w-[250px] h-[250px]",
	}

	return (
		<div className="w-full h-full flex flex-col border border-gray-300 px-[6px] py-[6px] overflow-x-auto">
			<div className="w-full flex flex-col gap-[4px]">
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<InputForm title="품목코드" value={editMST1010_R01?.itemCd || ''} editable={selectedMST1010_R01 === undefined} name="itemCd" onChange={handleChange} />
					<InputForm title="품명" value={editMST1010_R01?.itemNm || ''} editable={true} name="itemNm" onChange={handleChange} />
				</div>
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<InputForm title="규격" value={editMST1010_R01?.itemSpec || ''} editable={true} name="itemSpec" onChange={handleChange} />
					<InputForm title="모델명" value={editMST1010_R01?.modelNo || ''} editable={true} name="modelNo" onChange={handleChange} />
				</div>
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<DropDownForm title="브랜드" value={editMST1010_R01?.brandCd || ''} data={brandCdList} name="brandCd" initLabelLess={true} onChange={handleChange} />
					<DropDownForm title="단위" value={editMST1010_R01?.itemUnit || ''} data={unitList} name="itemUnit" initLabelLess={true} onChange={handleChange} />
				</div>
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<DropDownForm title="품목그룹" value={editMST1010_R01?.itemGroup || ''} data={itemGroupList} name="itemGroup" initLabelLess={true} onChange={handleChange} />
					<DropDownForm title="품목계정" value={editMST1010_R01?.itemAccount || ''} data={itemAccountList} name="itemAccount" initLabelLess={true} onChange={handleChange} />
				</div>
				<FlexFormBreak heightPx={15} />
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<InputForm
						title="표준 원가"
						value={editMST1010_R01?.itemCost ?? 0}
						editable={true}
						wrapperOption={infoInputStyle}
						name="itemCost"
						onChange={handleChange}
						fractionDigits={2}
						numericCommitOnBlur={true}
					/>
				</div>
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<InputForm title="포장 단위량" value={editMST1010_R01?.packQty ?? 0} editable={true} wrapperOption={infoInputStyle} name="packQty" onChange={handleChange} />
					<InputForm title="발주 단위량" value={editMST1010_R01?.purQty ?? 0} editable={true} wrapperOption={infoInputStyle} name="purQty" onChange={handleChange} />
				
				</div>

				<div className="w-full flex flex-wrap gap-x-[12px]">
				<InputForm title="수주 단위량" value={editMST1010_R01?.salQty ?? 0} editable={true} wrapperOption={infoInputStyle} name="salQty" onChange={handleChange} />
					<CodeForm
						label="location"
						value={editMST1010_R01?.locationCd || ""}
						custNmValue={editMST1010_R01?.locationNm || ""}
						editable={true}
						handleFoucsId={locationInfoHandleFoucsId}
						name="locationCd"
						handleChange={getFieldHandlers('locationCd').onChange}
						onDoubleClick={getFieldHandlers('locationCd').onDoubleClick}
						onKeyDown={getFieldHandlers('locationCd').onEnterKeyDown}
					/>
				</div>
				<FlexFormBreak heightPx={15} />
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<CodeForm
						label="거래처"
						value={editMST1010_R01?.customCd || ""}
						custNmValue={editMST1010_R01?.customNm || ""}
						editable={true}
						handleFoucsId={custInfoHandleFoucsId}
						name="customCd"
						handleChange={getFieldHandlers('customCd').onChange}
						onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
						onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
					/>
					<InputForm
						title="발주처 제품코드"
						value={editMST1010_R01?.customItemcd || ''}
						editable={true}
						wrapperOption={{
							wrapperStyle: "w-[100px] min-w-[100px] mr-[10px] mb-[5px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-[200px] min-w-[200px] text-right"
						}}
						name="customItemcd"
						onChange={handleChange}
					/>
				</div>
				<div className="w-full flex flex-wrap gap-x-[12px]">
					<DropDownForm
						title="생산재고 발생"
						value={editMST1010_R01?.prodStockType || ''}
						data={prodStockTypeList}
						name="prodStockType"
						initLabelLess={true}
						onChange={handleChange}
					/>
					<DropDownForm
						title="품질검사 필수"
						value={editMST1010_R01?.inspectYn || ''}
						data={yesNoList}
						name="inspectYn"
						initLabelLess={true}
						onChange={handleChange}
					/>
				</div>
			</div>
			<FlexFormBreak heightPx={15} />
			<div className="w-full shrink-0 flex flex-wrap gap-[12px] pt-[12px]">
				<ImageForm title="품목사진" image={editMST1010_R01?.itemImage || ''} onImageChange={(itemImage) => setEditMST1010_R01({ ...(editMST1010_R01 ?? defaultMST1010_R01Dto), itemImage })} wrapperOption={itemImageWrapperStyle} />
				<ImageForm title="품목사진2" image={editMST1010_R01?.itemImage2 || ''} onImageChange={(itemImage2) => setEditMST1010_R01({ ...(editMST1010_R01 ?? defaultMST1010_R01Dto), itemImage2 })} wrapperOption={itemImageWrapperStyle} />
			</div>
		</div>
	)
}
